/**
 * Centralized Error Handler with Error Boundary pattern
 * Provides consistent error handling across the application
 */

import { CONFIG, ENV } from './config.js';
import eventBus, { EVENTS } from './event-bus.js';

class ErrorHandler {
    constructor() {
        this.errorCount = 0;
        this.maxErrors = CONFIG.ERROR.MAX_ERRORS;
        this.setupGlobalErrorHandlers();
    }

    /**
     * Setup global error handlers for uncaught errors
     */
    setupGlobalErrorHandlers() {
        // Handle uncaught JavaScript errors
        window.addEventListener('error', event => {
            this.handleError(event.error || event.message, {
                type: 'javascript',
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                stack: event.error?.stack
            });
        });

        // Handle unhandled promise rejections
        window.addEventListener('unhandledrejection', event => {
            this.handleError(event.reason, {
                type: 'promise',
                stack: event.reason?.stack
            });
        });
    }

    /**
     * Handle an error with context information
     * @param {Error|string} error - The error object or message
     * @param {Object} context - Additional context information
     */
    handleError(error, context = {}) {
        this.errorCount++;

        // Prevent error spam
        if (this.errorCount > this.maxErrors) {
            // eslint-disable-next-line no-console
            console.warn('Too many errors, suppressing further error reports');
            return;
        }

        const errorInfo = this.formatError(error, context);

        // Log error to console in development
        if (this.isDevelopment()) {
            // eslint-disable-next-line no-console
            console.error('Application Error:', errorInfo);
        }

        // Emit error event for other modules to handle
        eventBus.emit(EVENTS.APP_ERROR, errorInfo);

        // Handle specific error types
        this.handleSpecificErrors(error, context);
    }

    /**
     * Format error information for consistent reporting
     * @param {Error|string} error - The error
     * @param {Object} context - Context information
     * @returns {Object} Formatted error information
     */
    formatError(error, context) {
        const timestamp = new Date().toISOString();
        const userAgent = navigator.userAgent;
        const url = window.location.href;

        return {
            timestamp,
            message: error?.message || error || 'Unknown error',
            stack: error?.stack,
            type: context.type || 'unknown',
            context,
            userAgent,
            url,
            errorCount: this.errorCount
        };
    }

    /**
     * Handle specific types of errors
     * @param {Error|string} error - The error
     * @param {Object} context - Context information
     */
    handleSpecificErrors(error) {
        // context parameter is reserved for future use
        const message = error?.message || error || '';

        // Handle network errors
        if (message.includes('NetworkError') || message.includes('fetch')) {
            this.showUserMessage('Chyba připojení k serveru. Zkontrolujte internetové připojení.');
            return;
        }

        // Handle authentication errors
        if (message.includes('401') || message.includes('Unauthorized')) {
            this.handleAuthenticationError();
            return;
        }

        // Handle permission errors
        if (message.includes('403') || message.includes('Forbidden')) {
            this.showUserMessage(CONFIG.ERROR.ACCESS_DENIED_MESSAGE);
            return;
        }

        // Handle generic errors
        this.showUserMessage(CONFIG.ERROR.FALLBACK_MESSAGE);
    }

    /**
     * Handle authentication errors
     */
    handleAuthenticationError() {
        eventBus.emit(EVENTS.USER_LOGGED_OUT);
        this.showUserMessage(CONFIG.ERROR.SESSION_EXPIRED_MESSAGE);

        // Redirect to login after delay
        setTimeout(() => {
            const returnUrl = window.location.pathname + (window.location.search || '');
            const loginUrl =
                CONFIG.ERROR.LOGIN_URL +
                (returnUrl !== '/' ? `?ReturnUrl=${encodeURIComponent(returnUrl)}` : '');
            window.location.replace(loginUrl);
        }, CONFIG.ERROR.REDIRECT_DELAY_MS);
    }

    /**
     * Show error message to user
     * @param {string} message - Error message
     */
    showUserMessage(message) {
        // Use toastr if available, otherwise fallback to alert
        if (typeof toastr !== 'undefined') {
            toastr.error(message);
        } else {
            alert(message);
        }
    }

    /**
     * Check if running in development environment
     * @returns {boolean}
     */
    isDevelopment() {
        return ENV.isDevelopment();
    }

    /**
     * Wrap a function with error handling
     * @param {Function} fn - Function to wrap
     * @param {string} context - Context description
     * @returns {Function} Wrapped function
     */
    wrapFunction(fn, context = 'unknown') {
        return (...args) => {
            try {
                const result = fn(...args);

                // Handle async functions
                if (result instanceof Promise) {
                    return result.catch(error => {
                        this.handleError(error, { type: 'async', context });
                        throw error;
                    });
                }

                return result;
            } catch (error) {
                this.handleError(error, { type: 'sync', context });
                throw error;
            }
        };
    }

    /**
     * Create error boundary for DOM operations
     * @param {Function} operation - DOM operation to perform
     * @param {string} context - Context description
     * @returns {*} Result of operation or null if error
     */
    safeDOMOperation(operation, context = 'DOM operation') {
        try {
            return operation();
        } catch (error) {
            this.handleError(error, { type: 'dom', context });
            return null;
        }
    }

    /**
     * Reset error counter (useful for testing)
     */
    resetErrorCount() {
        this.errorCount = 0;
    }

    /**
     * Get current error count
     * @returns {number}
     */
    getErrorCount() {
        return this.errorCount;
    }
}

// Create singleton instance
const errorHandler = new ErrorHandler();

// Export both the class and the singleton instance
export { ErrorHandler };
export default errorHandler;

// Convenience functions for common error handling patterns
export const handleError = (error, context) => errorHandler.handleError(error, context);
export const wrapFunction = (fn, context) => errorHandler.wrapFunction(fn, context);
export const safeDOMOperation = (operation, context) =>
    errorHandler.safeDOMOperation(operation, context);

/**
 * Get caller function name from stack trace
 * @returns {string} Function name or 'unknown'
 */
function getCallerFunctionName() {
    try {
        const stack = new Error().stack;
        if (!stack) return 'unknown';

        const stackLines = stack.split('\n');
        // Skip: Error, getCallerFunctionName, logError/logWarning/etc, actual caller
        const callerLine = stackLines[4];
        if (!callerLine) return 'unknown';

        // Extract function name from stack trace
        // Format: "    at functionName (file:line:column)" or "    at Object.functionName (file:line:column)"
        const match = callerLine.match(/at\s+(?:(\w+)\.)?(\w+)\s+\(/);
        if (match) {
            return match[2] || match[1] || 'unknown';
        }

        // Fallback: try to extract from "at functionName" format
        const simpleMatch = callerLine.match(/at\s+(\w+)/);
        return simpleMatch ? simpleMatch[1] : 'unknown';
    } catch (error) {
        return 'unknown';
    }
}

/**
 * Get caller class name from stack trace
 * @returns {string} Class name or 'unknown'
 */
function getCallerClassName() {
    try {
        const stack = new Error().stack;
        if (!stack) return 'unknown';

        const stackLines = stack.split('\n');
        // Skip: Error, getCallerClassName, logError/logWarning/etc, actual caller
        const callerLine = stackLines[4];
        if (!callerLine) return 'unknown';

        // Extract class name from stack trace
        // Format: "    at ClassName.methodName (file:line:column)"
        const match = callerLine.match(/at\s+(\w+)\.(\w+)\s+\(/);
        if (match) {
            return match[1]; // Return class name
        }

        return 'unknown';
    } catch (error) {
        return 'unknown';
    }
}

/**
 * Merge automatic context with provided context
 * @param {Object} providedContext - Context provided by user
 * @returns {Object} Merged context with automatic function name and class name
 */
function mergeContext(providedContext = {}) {
    const automaticContext = {
        function: getCallerFunctionName(),
        className: getCallerClassName()
    };

    // If user provided context, merge it (user context takes precedence)
    return {
        ...automaticContext,
        ...providedContext
    };
}

// One-line error handling functions for minimal code overhead
export const logError = (error, context = {}) => {
    const mergedContext = mergeContext(context);
    return errorHandler.handleError(error, mergedContext);
};

export const logWarning = (message, context = {}) => {
    const mergedContext = mergeContext(context);
    return errorHandler.handleError(new Error(message), { ...mergedContext, type: 'warning' });
};

export const logInfo = (message, context = {}) => {
    if (errorHandler.isDevelopment()) {
        const mergedContext = mergeContext(context);
        // eslint-disable-next-line no-console
        console.info(`[INFO] ${message}`, mergedContext);
    }
};

// Additional logging functions for development
export const logDebug = (message, context = {}) => {
    if (errorHandler.isDevelopment()) {
        const mergedContext = mergeContext(context);
        // eslint-disable-next-line no-console
        console.log(`[DEBUG] ${message}`, mergedContext);
    }
};

export const logEmoji = (emoji, message, context = {}) => {
    if (errorHandler.isDevelopment()) {
        const mergedContext = mergeContext(context);
        // eslint-disable-next-line no-console
        console.log(`${emoji} ${message}`, mergedContext);
    }
};

export const log = (message, context = {}) => {
    if (errorHandler.isDevelopment()) {
        const mergedContext = mergeContext(context);
        // eslint-disable-next-line no-console
        console.log(message, mergedContext);
    }
};

// Safe execution helpers - execute function and log errors automatically
export const safeExecute = (fn, context = null, fallback = null) => {
    try {
        const result = fn();
        if (result instanceof Promise) {
            return result.catch(error => {
                const mergedContext = context
                    ? mergeContext(context)
                    : mergeContext({ type: 'async' });
                errorHandler.handleError(error, mergedContext);
                return fallback;
            });
        }
        return result;
    } catch (error) {
        const mergedContext = context ? mergeContext(context) : mergeContext({ type: 'sync' });
        errorHandler.handleError(error, mergedContext);
        return fallback;
    }
};

// Safe async execution
export const safeExecuteAsync = async (fn, context = null, fallback = null) => {
    try {
        return await fn();
    } catch (error) {
        const mergedContext = context ? mergeContext(context) : mergeContext({ type: 'async' });
        errorHandler.handleError(error, mergedContext);
        return fallback;
    }
};

// Safe DOM operations with automatic error handling
export const safeDOM = (operation, context = null, fallback = null) => {
    const mergedContext = context ? mergeContext(context) : mergeContext({ type: 'dom' });
    return errorHandler.safeDOMOperation(operation, mergedContext) || fallback;
};

// Safe AJAX operations
export const safeAjax = (ajaxOptions, context = null) => {
    return new Promise((resolve, reject) => {
        try {
            const mergedContext = context ? mergeContext(context) : mergeContext({ type: 'ajax' });

            $.ajax({
                ...ajaxOptions,
                success: (data, textStatus, jqXHR) => {
                    try {
                        if (ajaxOptions.success) {
                            ajaxOptions.success(data, textStatus, jqXHR);
                        }
                        resolve(data);
                    } catch (error) {
                        errorHandler.handleError(error, { ...mergedContext, type: 'ajax_success' });
                        reject(error);
                    }
                },
                error: (jqXHR, textStatus, errorThrown) => {
                    errorHandler.handleError(new Error(`AJAX Error: ${textStatus}`), {
                        ...mergedContext,
                        type: 'ajax_error',
                        jqXHR,
                        textStatus,
                        errorThrown
                    });
                    if (ajaxOptions.error) {
                        ajaxOptions.error(jqXHR, textStatus, errorThrown);
                    }
                    reject(new Error(textStatus));
                }
            });
        } catch (error) {
            const mergedContext = context
                ? mergeContext(context)
                : mergeContext({ type: 'ajax_setup' });
            errorHandler.handleError(error, mergedContext);
            reject(error);
        }
    });
};
